// Incoming-call ringtone, generated with the Web Audio API so no binary audio asset is
// shipped (design/05 section 3.4). A single ~480 Hz tone is pulsed in a telephone-style
// on/off cadence. Fully guarded: if Web Audio is unavailable or the browser blocks autoplay,
// the service degrades to a silent no-op and never throws - the call UI must never break.
//
// The AudioContext is injected through a factory so the control logic (start/stop/
// idempotency/fallback) is unit-testable with a fake, with no real audio device.

/** Minimal Web Audio surface used by the ringtone - the real AudioContext and the test fake both satisfy it. */
export interface RingAudioParam {
  value: number;
  setValueAtTime(value: number, when: number): void;
  cancelScheduledValues(when: number): void;
}
export interface RingOscillator {
  type: string;
  frequency: RingAudioParam;
  connect(dest: unknown): void;
  start(): void;
  stop(): void;
  disconnect(): void;
}
export interface RingGain {
  gain: RingAudioParam;
  connect(dest: unknown): void;
  disconnect(): void;
}
export interface RingAudioContext {
  readonly currentTime: number;
  readonly destination: unknown;
  resume?(): Promise<void>;
  close?(): Promise<void>;
  createOscillator(): RingOscillator;
  createGain(): RingGain;
}

export type RingAudioFactory = () => RingAudioContext | null;

export interface Ringtone {
  start(): void;
  stop(): void;
  readonly playing: boolean;
}

/** Default factory: a browser AudioContext, or null when Web Audio is unavailable. */
export function defaultRingAudioFactory(): RingAudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor() as unknown as RingAudioContext;
  } catch {
    return null;
  }
}

const RING_HZ = 480; // classic telephone ring frequency
const RING_GAIN = 0.18;
const SILENT = 0.0001;
const BURST_PERIOD_MS = 3000; // one ring burst every 3 s

/**
 * Create an incoming-call ringtone. <c>start()</c> is idempotent and never throws;
 * <c>stop()</c> is safe to call even if never started.
 */
export function createRingtone(factory: RingAudioFactory = defaultRingAudioFactory): Ringtone {
  let ctx: RingAudioContext | null = null;
  let osc: RingOscillator | null = null;
  let gain: RingGain | null = null;
  let loop: ReturnType<typeof setInterval> | null = null;
  let playing = false;

  // One ring burst: a short on/off gain envelope scheduled on the audio clock.
  const burst = (): void => {
    if (!ctx || !gain) return;
    const t = ctx.currentTime;
    const g = gain.gain;
    try {
      g.cancelScheduledValues(t);
      g.setValueAtTime(SILENT, t);
      g.setValueAtTime(RING_GAIN, t + 0.02); // ring on
      g.setValueAtTime(RING_GAIN, t + 0.9);
      g.setValueAtTime(SILENT, t + 1.0); // ring off until the next burst
    } catch {
      /* scheduling unsupported - leave the tone at its current level */
    }
  };

  const teardown = (): void => {
    if (loop) {
      clearInterval(loop);
      loop = null;
    }
    try { osc?.stop(); } catch { /* already stopped */ }
    try { osc?.disconnect(); } catch { /* ignore */ }
    try { gain?.disconnect(); } catch { /* ignore */ }
    try { void ctx?.close?.(); } catch { /* ignore */ }
    osc = null;
    gain = null;
    ctx = null;
    playing = false;
  };

  return {
    get playing() {
      return playing;
    },
    start() {
      if (playing) return; // idempotent - a second start does not stack tones
      try {
        const c = factory();
        if (!c) return; // Web Audio unavailable -> silent no-op
        ctx = c;
        // Autoplay policy may leave the context suspended until a user gesture; resume is
        // best-effort. resume() is optional and may be absent, so guard the call AND the
        // promise: c.resume?.() yields undefined when absent, and undefined?.catch is a no-op.
        const resuming = c.resume?.();
        resuming?.catch(() => undefined); // swallow rejection (no sound, but no error either)
        osc = c.createOscillator();
        gain = c.createGain();
        osc.type = "sine";
        osc.frequency.value = RING_HZ;
        gain.gain.value = SILENT;
        osc.connect(gain);
        gain.connect(c.destination);
        osc.start();
        playing = true;
        burst();
        loop = setInterval(burst, BURST_PERIOD_MS);
      } catch {
        // Any setup failure -> tear down cleanly and stay silent.
        teardown();
      }
    },
    stop() {
      teardown();
    }
  };
}
